import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { AlertTriangle } from "lucide-react";

/**
 * Reusable destructive-action confirmation modal.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (bool) => void
 *   entityType: string (e.g. "licencié principal", "lead", "commerçant")
 *   entityLabel: string  (the value the admin must retype, e.g. email or "Prénom Nom")
 *   description: string  (line above the consequences list)
 *   consequences: string[] (bullet points listed under "Conséquences :")
 *   confirmText: optional — defaults to "Supprimer définitivement"
 *   onConfirm: async () => void
 */
export function ConfirmDeleteModal({
  open,
  onOpenChange,
  entityType,
  entityLabel,
  description,
  consequences = [],
  confirmText = "Supprimer définitivement",
  onConfirm,
}) {
  const [input, setInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const matches = input === entityLabel && entityLabel.length > 0;

  useEffect(() => {
    if (!open) {
      setInput("");
      setSubmitting(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!matches || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // Caller is responsible for surfacing toast errors. Re-enable button.
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="confirm-delete-modal" className="max-w-md">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center mb-2">
            <AlertTriangle className="w-6 h-6 text-[#B91C1C]" />
          </div>
          <DialogTitle className="font-display text-xl text-[#1F2937]">
            Supprimer ce {entityType} ?
          </DialogTitle>
          <DialogDescription className="text-[#4B5563] leading-relaxed pt-1">
            {description}
          </DialogDescription>
        </DialogHeader>

        {consequences.length > 0 && (
          <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm">
            <div className="text-xs uppercase tracking-wider font-semibold text-[#92400E] mb-2">
              Conséquences
            </div>
            <ul className="space-y-1.5 text-[#78350F]">
              {consequences.map((c, i) => (
                <li key={i} className="flex gap-2 items-start">
                  <span className="text-[#92400E] mt-0.5">•</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-1.5 pt-2">
          <Label htmlFor="confirm-input" className="text-sm text-[#1F2937]">
            Pour confirmer, retapez exactement :{" "}
            <code className="font-mono text-[#B91C1C] font-semibold">{entityLabel}</code>
          </Label>
          <Input
            id="confirm-input"
            data-testid="confirm-delete-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoComplete="off"
            placeholder={entityLabel}
            className="font-mono"
          />
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="confirm-delete-cancel"
            className="rounded-lg"
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!matches || submitting}
            data-testid="confirm-delete-confirm"
            className="rounded-lg bg-[#B91C1C] hover:bg-[#991B1B] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "Suppression…" : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmDeleteModal;
